import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, X } from "lucide-react";
import { DUOCLAIR_WHATSAPP } from "@/config";

export const SiteMenu = () => {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstRef.current?.focus(), 150);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [open]);

  // Chiude il menu ad ogni cambio di pagina
  useEffect(() => {
    setOpen(false);
    setContacts(false);
  }, [location.pathname]);

  const go = (action) => () => {
    setOpen(false);
    action();
  };

  const items = [
    { id: "home", label: "Home", active: location.pathname === "/", onClick: go(() => navigate("/")) },
    { id: "video", label: "Guarda il video", onClick: go(() => navigate("/", { state: { scrollTarget: "#video" } })) },
    { id: "servizi", label: "I nostri servizi", active: location.pathname === "/servizi", onClick: go(() => navigate("/servizi")) },
    { id: "analisi", label: "Analisi gratuita", onClick: go(() => navigate("/", { state: { openForm: true } })) },
  ];

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="site-menu-panel"
        aria-label={open ? "Chiudi menu" : "Apri menu"}
        data-testid="site-menu-button"
        className="relative z-[95] w-10 h-10 shrink-0 flex flex-col items-center justify-center gap-[5px] rounded-full border border-white/15 bg-black/40 hover:border-gold/50 transition-colors duration-300"
      >
        <span className={`block h-px w-4 bg-slate-200 transition-all duration-300 ${open ? "translate-y-[6px] rotate-45 bg-gold-light" : ""}`} />
        <span className={`block h-px w-4 bg-slate-200 transition-all duration-300 ${open ? "opacity-0" : ""}`} />
        <span className={`block h-px w-4 bg-slate-200 transition-all duration-300 ${open ? "-translate-y-[6px] -rotate-45 bg-gold-light" : ""}`} />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              id="site-menu-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Menu di navigazione"
              data-testid="site-menu-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-md"
              onClick={(e) => e.target === e.currentTarget && setOpen(false)}
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
                data-testid="site-menu-close"
                className="absolute top-4 right-4 sm:top-5 sm:right-6 w-10 h-10 grid place-items-center rounded-full border border-white/15 bg-black/50 text-slate-200 hover:border-gold/50 hover:text-gold-light transition-colors"
              >
                <X className="w-4.5 h-4.5" />
              </button>
              <nav className="h-full overflow-y-auto flex flex-col items-center justify-center px-8 py-16">
              <ul className="w-full max-w-xs space-y-1.5">
                {items.map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.06 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <button
                      ref={i === 0 ? firstRef : undefined}
                      onClick={item.onClick}
                      aria-current={item.active ? "page" : undefined}
                      data-testid={`menu-item-${item.id}`}
                      className={`w-full text-left rounded-xl px-5 py-3.5 font-sans font-semibold text-lg tracking-tight transition-colors duration-200 border ${
                        item.active
                          ? "text-gold-light border-gold/40 bg-gold/[0.08]"
                          : "text-slate-100 border-transparent hover:text-gold-light hover:border-white/10 hover:bg-white/[0.04]"
                      }`}
                    >
                      {item.label}
                    </button>
                  </motion.li>
                ))}

                <motion.li
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.06 + items.length * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    onClick={() => setContacts((v) => !v)}
                    aria-expanded={contacts}
                    aria-controls="menu-contacts"
                    data-testid="menu-item-contatti"
                    className={`w-full text-left rounded-xl px-5 py-3.5 font-sans font-semibold text-lg tracking-tight transition-colors duration-200 border ${
                      contacts
                        ? "text-gold-light border-gold/40 bg-gold/[0.08]"
                        : "text-slate-100 border-transparent hover:text-gold-light hover:border-white/10 hover:bg-white/[0.04]"
                    }`}
                  >
                    Contatti
                  </button>
                  <AnimatePresence initial={false}>
                    {contacts && (
                      <motion.div
                        id="menu-contacts"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-2 px-2 pt-2 pb-1">
                          <a
                            href={DUOCLAIR_WHATSAPP.link}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="menu-contacts-whatsapp"
                            className="inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-200 hover:border-gold/40 hover:text-gold-light transition-colors"
                          >
                            <MessageCircle className="w-4 h-4 text-gold" /> WhatsApp Business · {DUOCLAIR_WHATSAPP.display}
                          </a>
                          <a
                            href="https://instagram.com/duoclair"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="menu-contacts-instagram"
                            className="inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-200 hover:border-gold/40 hover:text-gold-light transition-colors"
                          >
                            <Instagram className="w-4 h-4 text-gold" /> Instagram · @duoclair
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.06 + (items.length + 1) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    onClick={go(() => navigate("/admin/login"))}
                    data-testid="menu-item-partner"
                    className="w-full text-left rounded-xl px-5 py-3.5 font-sans font-semibold text-base tracking-tight text-zinc-400 border border-transparent hover:text-gold-light hover:border-white/10 hover:bg-white/[0.04] transition-colors duration-200"
                  >
                    Accesso Partner
                  </button>
                </motion.li>
              </ul>
            </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
